/**
 * PatientFocusPanel — what a patient needs to see today.
 *
 * Note: this powers the PATIENT's OWN dashboard at /patient/dashboard.
 * The Family-as-View surface (a caregiver looking IN at a loved one)
 * lives at /family per Part A spec and is intentionally minimal in
 * its own right — we never render this panel for a family caregiver
 * inside the family view.
 */
import React from 'react';
import { CalendarClock, Stethoscope } from 'lucide-react';
import { FocusCard, FocusHero } from './FocusCard';

export function PatientFocusPanel({ focus }) {
  const blocks = focus?.blocks || [];
  const byKey = Object.fromEntries(blocks.map((b) => [b.key, b]));

  return (
    <div className="space-y-6" data-testid="patient-focus-panel">
      <FocusHero
        title="A calmer way to track your care."
        subtitle="Your next visit and active placement — nothing else, until you ask."
        primaryAction={focus?.primary_action}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FocusCard
          block={byKey.next_visit}
          testIdPrefix="patient-focus"
          emptyHint="No upcoming visit scheduled yet."
          renderRow={(r) => (
            <>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 inline-flex items-center gap-1">
                  <CalendarClock className="w-4 h-4 text-slate-400" />
                  {r?.first_visit_at ? new Date(r.first_visit_at).toLocaleString() : '—'}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  In about {r?.hours_until ?? '—'} hour{r?.hours_until === 1 ? '' : 's'}
                </div>
              </div>
              <a href={`/patient/dashboard`}
                  className="text-xs text-slate-600 hover:text-slate-900">
                Details →
              </a>
            </>
          )}
        />

        <FocusCard
          block={byKey.active_placement}
          testIdPrefix="patient-focus"
          emptyHint="No active care placement."
          renderRow={(r) => (
            <>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 inline-flex items-center gap-1">
                  <Stethoscope className="w-4 h-4 text-slate-400" />
                  {Array.isArray(r?.ordered_services) && r.ordered_services.length > 0
                    ? r.ordered_services.join(', ')
                    : 'Care plan in setup'}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Status: {r?.status || '—'}
                </div>
              </div>
              <a href={`/patient/dashboard`}
                  className="text-xs text-slate-600 hover:text-slate-900">
                Details →
              </a>
            </>
          )}
        />
      </div>
    </div>
  );
}
